import type { PositionsChangeStatusDto } from '@/services/ws-post/src/main/resources/rest-api';
import { getPositionsChangeStatus, PositionsChangeStatus } from '@/services/ws-post/src/main/resources/rest-api';

import type { AsyncChangeApiMethod, WaitForAsyncChange } from './useWaitForAsyncChange.ts';
import { useWaitForAsyncChange } from './useWaitForAsyncChange.ts';

interface Props<TResult> {
  /** Left out by a reconciliation, which stores no result - its positions are re-read with the ordinary GET. */
  resultApiMethod?: AsyncChangeApiMethod<TResult>;
}

/**
 * Waits for the email or addressed mailing positions change that the four step 5 `PATCH` endpoints run in the
 * background when they answer 202.
 *
 * A failure is thrown rather than notified about, so it travels the path a 4xx of the save itself takes -
 * `usePositionsState` catch, `onError`, `ParentOrchestraForm` `onSubmitError`, `getPostErrorMessage` - and ends up
 * rendered as `t('backend:<translationKey>')`, in the same place and with the same styling as before.
 */
export const useWaitForPositionsChange = <TResult = void>({ resultApiMethod }: Props<TResult> = {}): WaitForAsyncChange<TResult> =>
  useWaitForAsyncChange({
    statusApiMethod: getPositionsChangeStatus,
    resultApiMethod,
    statusValues: PositionsChangeStatus,
    // the form reports every rejection of the wait, so the request layer must not report a failed poll on top of it
    isFailureReportedByCaller: true,
    onError: ({ translationKey }: PositionsChangeStatusDto) => {
      // the backend names a reason for every failure it reports; the fallback only keeps the message translatable
      throw new Error(translationKey ?? 'ASYNC_POSITION_FAILED');
    },
  });
